import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getCountFromServer,
    getDocs,
    getFirestore,
    limit,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    startAfter,
} from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { COLLECTIONS } from '@constants/firestorePaths';

function db() {
    return getFirestore(getFirebaseApp());
}

function getHomeworksCollectionRef(tenantId) {
    if (!tenantId) throw new Error('Missing tenantId');
    return collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.HOMEWORKS);
}

function mapDoc(snap) {
    return { id: snap.id, ...snap.data() };
}

function normalizeTodos(todos) {
    if (!Array.isArray(todos)) return [];
    return todos
        .map((todo, index) => {
            const title = String(todo?.title || '').trim();
            const note = String(todo?.note || '').trim();
            const required = !!todo?.required;
            if (!title) return null;
            return {
                id: todo?.id || `todo-${index + 1}`,
                title,
                note: note || null,
                required,
            };
        })
        .filter(Boolean);
}

export async function fetchHomeworksPage({ tenantId, pageSize = 10, cursor = null }) {
    const baseRef = getHomeworksCollectionRef(tenantId);
    const countSnap = await getCountFromServer(baseRef);
    const q = cursor
        ? query(baseRef, orderBy('createdAt', 'desc'), startAfter(cursor), limit(pageSize))
        : query(baseRef, orderBy('createdAt', 'desc'), limit(pageSize));
    const snap = await getDocs(q);
    const rows = snap.docs.map(mapDoc);
    const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
    return { rows, nextCursor, total: countSnap.data().count || 0 };
}

export async function createHomework({ tenantId, payload, createdBy }) {
    const baseRef = getHomeworksCollectionRef(tenantId);
    const prepared = {
        ...payload,
        todos: normalizeTodos(payload?.todos),
        tenantId,
        createdBy: createdBy || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    };
    const created = await addDoc(baseRef, prepared);
    return { id: created.id };
}

export async function updateHomework({ tenantId, homeworkId, patch }) {
    if (!homeworkId) throw new Error('Missing homeworkId');
    const homeworkRef = doc(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.HOMEWORKS, homeworkId);
    await setDoc(
        homeworkRef,
        {
            ...patch,
            ...(Array.isArray(patch?.todos) ? { todos: normalizeTodos(patch.todos) } : {}),
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
    return { homeworkId };
}

export async function deleteHomework({ tenantId, homeworkId }) {
    if (!homeworkId) throw new Error('Missing homeworkId');
    const homeworkRef = doc(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.HOMEWORKS, homeworkId);
    await deleteDoc(homeworkRef);
    return { homeworkId };
}
